// Platform analytics (approved mockup "Platform Analytics", 2026-10-11): how
// every company actually uses CITS Time, read from each company's own data
// by CITS-Server services/platformAnalytics (GET /super-admin/analytics/overview).
// KPIs with change vs the previous window, people on the clock over time,
// plans + trials ending, companies with health flags (needs attention first),
// feature use (plan includes vs used) and app versions.
import { useCallback, useEffect, useState } from 'react'
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts'
import { ArrowPathIcon } from '@heroicons/react/24/outline'
import api from '../../api/axios'

type Range = 'today' | '7d' | '30d' | '90d'
type Level = 'bad' | 'warn' | 'ok' | 'info'

interface Change { value: number; change: number | null }
interface CompanyRow {
  id: string
  name: string
  code: string
  status: string
  tier: string
  lastClockIn: string | null
  onClock: number
  people: number
  shifts: number
  hours: number
  deliveries: number
  spark: number[]
  phones: number
  versions: { version: string; count: number }[]
  appErrors: number
  health: { primary: { level: Level; text: string }; flags: { level: Level; text: string }[] }
}
interface Overview {
  range: Range
  generatedAt: string
  kpis: {
    companiesActive: number
    companiesTotal: number
    onClockNow: number
    shifts: Change
    hours: Change
    deliveries: Change
    phonesOnLatest: number | null
    latestVersion: string | null
  }
  series: { unit: 'hour' | 'day'; points: { t: string; value: number }[] }
  plans: { tier: string; count: number }[]
  trialsEnding: { name: string; daysLeft: number }[]
  companies: CompanyRow[]
  features: { key: string; name: string; included: number; used: number }[]
  appVersions: { version: string; count: number }[]
}

const RANGES: { key: Range; label: string }[] = [
  { key: 'today', label: 'Today' },
  { key: '7d', label: '7 days' },
  { key: '30d', label: '30 days' },
  { key: '90d', label: '90 days' },
]

const PILL: Record<Level, string> = {
  bad: 'bg-red-50 text-red-700',
  warn: 'bg-amber-50 text-amber-700',
  ok: 'bg-emerald-50 text-emerald-700',
  info: 'bg-blue-50 text-blue-700',
}

const nf = new Intl.NumberFormat()

function ago(iso: string | null): string {
  if (!iso) return 'Never'
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.round(ms / 60000)
  if (m < 1) return 'Just now'
  if (m < 60) return `${m} min ago`
  const h = Math.round(m / 60)
  if (h < 24) return `${h} h ago`
  return new Date(iso).toLocaleString(undefined, { weekday: 'short', hour: 'numeric', minute: '2-digit' })
}

function ChangeText({ change }: { change: number | null }) {
  if (change == null) return <span className="text-xs font-semibold text-slate-400">No earlier data</span>
  const up = change >= 0
  return <span className={`text-xs font-bold ${up ? 'text-emerald-600' : 'text-red-600'}`}>{up ? '+' : ''}{change}% vs previous</span>
}

function Kpi({ label, value, sub }: { label: string; value: string; sub: React.ReactNode }) {
  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="text-2xl font-bold text-slate-900 mt-1 tabular-nums">{value}</div>
      <div className="mt-0.5">{sub}</div>
    </div>
  )
}

function Spark({ data }: { data: number[] }) {
  const max = Math.max(1, ...data)
  const pts = data.map((v, i) => `${4 + (i * 82) / Math.max(1, data.length - 1)},${22 - (v / max) * 18}`).join(' ')
  const last = data.length - 1
  return (
    <svg width="90" height="26" viewBox="0 0 90 26" aria-hidden="true">
      <polyline fill="none" stroke="#2563eb" strokeWidth="2" points={pts} />
      <circle cx={4 + (last * 82) / Math.max(1, last)} cy={22 - (data[last] / max) * 18} r="2.6" fill="#2563eb" />
    </svg>
  )
}

function VersionBar({ versions, phones }: { versions: CompanyRow['versions']; phones: number }) {
  if (!phones) return <span className="text-xs text-slate-400">No phones</span>
  const colors = ['#16a34a', '#86efac', '#f59e0b', '#dc2626']
  return (
    <div className="flex h-2 w-28 rounded overflow-hidden bg-slate-100" title={versions.map((v) => `${v.version}: ${v.count}`).join(', ')}>
      {versions.map((v, i) => (
        <div key={v.version} style={{ width: `${(v.count / phones) * 100}%`, background: colors[Math.min(i, 3)] }} />
      ))}
    </div>
  )
}

function Bars({ items, color = '#2563eb' }: { items: { label: string; value: number; max: number; right: string }[]; color?: string }) {
  return (
    <div className="space-y-2.5">
      {items.map((it) => (
        <div key={it.label} className="grid grid-cols-[110px_1fr_48px] items-center gap-2 text-sm">
          <span className="truncate text-slate-700">{it.label}</span>
          <div className="h-2.5 rounded bg-slate-100 overflow-hidden">
            <div className="h-full rounded" style={{ width: `${it.max ? (it.value / it.max) * 100 : 0}%`, background: color }} />
          </div>
          <span className="text-right font-semibold tabular-nums text-slate-800">{it.right}</span>
        </div>
      ))}
    </div>
  )
}

export default function PlatformAnalytics() {
  const [range, setRange] = useState<Range>('7d')
  const [data, setData] = useState<Overview | null>(null)
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async (r: Range) => {
    setLoading(true)
    setError(null)
    try {
      const res = await api.get('/super-admin/analytics/overview', { params: { range: r } })
      setData(res.data)
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } }
      setError(err.response?.data?.message || 'Could not load analytics')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load(range) }, [range, load])

  const refresh = async () => {
    setRefreshing(true)
    setError(null)
    try {
      const res = await api.post('/super-admin/analytics/refresh', { range })
      if (res.data?.overview) setData(res.data.overview)
    } catch (e) {
      const err = e as { response?: { data?: { message?: string } } }
      setError(err.response?.data?.message || 'Could not refresh')
    } finally {
      setRefreshing(false)
    }
  }

  const k = data?.kpis
  const unit = data?.series.unit || 'hour'
  const chart = (data?.series.points || []).map((p) => ({
    t: p.t,
    label: unit === 'hour'
      ? new Date(p.t).toLocaleString(undefined, { weekday: 'short', hour: 'numeric' })
      : new Date(p.t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
    value: p.value,
  }))
  const peak = chart.reduce<{ value: number; label: string } | null>((m, p) => (!m || p.value > m.value ? p : m), null)
  const planMax = Math.max(1, ...(data?.plans || []).map((p) => p.count))
  const verTotal = (data?.appVersions || []).reduce((n, v) => n + v.count, 0)

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex bg-white border border-slate-200 rounded-lg p-1 gap-1" role="group" aria-label="Range">
          {RANGES.map((r) => (
            <button key={r.key} type="button" onClick={() => setRange(r.key)} aria-pressed={range === r.key}
              className={`px-3 py-1.5 rounded-md text-sm font-semibold ${range === r.key ? 'bg-blue-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}>
              {r.label}
            </button>
          ))}
        </div>
        <button type="button" onClick={refresh} disabled={refreshing}
          className="inline-flex items-center gap-2 px-3 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50">
          <ArrowPathIcon className={`w-4 h-4 ${refreshing ? 'animate-spin' : ''}`} /> {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
        {data && <span className="text-xs text-slate-500">Updated {ago(data.generatedAt)}</span>}
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">{error}</div>}

      {loading && !data ? (
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3">
          {Array.from({ length: 6 }, (_, i) => <div key={i} className="h-24 rounded-xl bg-slate-100 animate-pulse" />)}
        </div>
      ) : data && k ? (
        <>
          <div className={`grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-3 ${loading ? 'opacity-60' : ''}`}>
            <Kpi label="Companies active" value={`${k.companiesActive} / ${k.companiesTotal}`} sub={<span className="text-xs text-slate-500">clocked in this period</span>} />
            <Kpi label="On the clock now" value={nf.format(k.onClockNow)} sub={<span className="text-xs text-slate-500">all companies</span>} />
            <Kpi label="Shifts" value={nf.format(k.shifts.value)} sub={<ChangeText change={k.shifts.change} />} />
            <Kpi label="Hours tracked" value={nf.format(k.hours.value)} sub={<ChangeText change={k.hours.change} />} />
            <Kpi label="Deliveries" value={nf.format(k.deliveries.value)} sub={<ChangeText change={k.deliveries.change} />} />
            <Kpi label="Phones on latest app" value={k.phonesOnLatest == null ? '—' : `${k.phonesOnLatest}%`} sub={<span className="text-xs text-slate-500">{k.latestVersion ? `newest ${k.latestVersion}` : 'no phones seen'}</span>} />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
            <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-5 shadow-sm min-w-0">
              <h3 className="text-sm font-semibold text-slate-900">Crews on the clock</h3>
              <p className="text-xs text-slate-500 mb-3">{unit === 'hour' ? 'People clocked in each hour' : 'People who clocked in each day'}, all companies</p>
              {chart.length ? (
                <div className="h-56">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={chart} margin={{ top: 8, right: 8, bottom: 0, left: -12 }}>
                      <defs>
                        <linearGradient id="onclock" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="#2563eb" stopOpacity={0.25} />
                          <stop offset="100%" stopColor="#2563eb" stopOpacity={0.02} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid stroke="#e2e8f0" vertical={false} />
                      <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#64748b' }} interval="preserveStartEnd" minTickGap={40} tickLine={false} axisLine={false} />
                      <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: '#64748b' }} tickLine={false} axisLine={false} width={40} />
                      <Tooltip formatter={(v) => [`${v} people`, 'On the clock']} labelStyle={{ color: '#0f172a' }} />
                      <Area type="monotone" dataKey="value" stroke="#2563eb" strokeWidth={2} fill="url(#onclock)" isAnimationActive={false} />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              ) : <p className="text-sm text-slate-400 py-10 text-center">No clock-ins in this period.</p>}
              {peak && peak.value > 0 && <p className="text-xs text-slate-500 mt-2">Peak: {peak.value} people, {peak.label}</p>}
            </div>

            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm min-w-0">
              <h3 className="text-sm font-semibold text-slate-900">Plans</h3>
              <p className="text-xs text-slate-500 mb-3">Companies by plan, and trials ending</p>
              <Bars items={(data.plans || []).map((p) => ({ label: p.tier.charAt(0).toUpperCase() + p.tier.slice(1), value: p.count, max: planMax, right: String(p.count) }))} />
              {data.trialsEnding.length > 0 && (
                <div className="mt-4 space-y-1.5">
                  {data.trialsEnding.slice(0, 5).map((t) => (
                    <div key={t.name} className="flex items-center justify-between text-sm">
                      <span className="truncate text-slate-700">{t.name}</span>
                      <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${t.daysLeft <= 14 ? PILL.warn : PILL.info}`}>
                        {t.daysLeft <= 0 ? 'Ended' : `${t.daysLeft} day${t.daysLeft === 1 ? '' : 's'} left`}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <div className="px-5 py-4 border-b border-slate-100">
              <h3 className="text-sm font-semibold text-slate-900">Companies</h3>
              <p className="text-xs text-slate-500">Needs attention first. Each row is read from that company's own database.</p>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[900px]">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
                    <th className="px-4 py-2 font-semibold">Company</th>
                    <th className="px-4 py-2 font-semibold">Health</th>
                    <th className="px-4 py-2 font-semibold">Last clock-in</th>
                    <th className="px-4 py-2 font-semibold text-right">People</th>
                    <th className="px-4 py-2 font-semibold text-right">Shifts</th>
                    <th className="px-4 py-2 font-semibold text-right">Hours</th>
                    <th className="px-4 py-2 font-semibold">7-day trend</th>
                    <th className="px-4 py-2 font-semibold">App versions</th>
                    <th className="px-4 py-2 font-semibold text-right">App errors</th>
                  </tr>
                </thead>
                <tbody>
                  {data.companies.map((c) => (
                    <tr key={c.id} className="border-t border-slate-100 align-middle">
                      <td className="px-4 py-2.5"><div className="font-semibold text-slate-900">{c.name}</div><div className="text-xs text-slate-500">{c.code} · {c.tier}</div></td>
                      <td className="px-4 py-2.5">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded-full ${PILL[c.health.primary.level]}`} title={c.health.flags.map((f) => f.text).join(' · ')}>{c.health.primary.text}</span>
                      </td>
                      <td className="px-4 py-2.5 text-slate-700">{ago(c.lastClockIn)}{c.onClock > 0 && <span className="ml-1.5 text-xs text-emerald-600 font-semibold">{c.onClock} on now</span>}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{nf.format(c.people)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{nf.format(c.shifts)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{nf.format(c.hours)}</td>
                      <td className="px-4 py-2.5"><Spark data={c.spark} /></td>
                      <td className="px-4 py-2.5"><VersionBar versions={c.versions} phones={c.phones} /></td>
                      <td className={`px-4 py-2.5 text-right tabular-nums ${c.appErrors > 0 ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>{c.appErrors}</td>
                    </tr>
                  ))}
                  {data.companies.length === 0 && (
                    <tr><td colSpan={9} className="px-4 py-10 text-center text-slate-400">No active companies.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm min-w-0">
              <h3 className="text-sm font-semibold text-slate-900">Feature use</h3>
              <p className="text-xs text-slate-500 mb-3">Companies whose plan includes the feature, and how many used it this period</p>
              {data.features.length ? (
                <Bars items={data.features.map((f) => ({ label: f.name, value: f.used, max: f.included, right: `${f.used}/${f.included}` }))} />
              ) : <p className="text-sm text-slate-400">No plan data yet.</p>}
            </div>
            <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm min-w-0">
              <h3 className="text-sm font-semibold text-slate-900">App versions</h3>
              <p className="text-xs text-slate-500 mb-3">Phones by version, seen this period</p>
              {verTotal ? (
                <Bars
                  color="#16a34a"
                  items={data.appVersions.map((v) => ({ label: v.version, value: v.count, max: verTotal, right: nf.format(v.count) }))}
                />
              ) : <p className="text-sm text-slate-400">No phones seen in this period.</p>}
            </div>
          </div>
        </>
      ) : null}
    </div>
  )
}
